<template>
    <div>
        <template v-if="loaded">
            <rkm-form show-legend>
                <rkm-area title="Dados do documento">
                    <rkm-form ref="Form">
                        <rkm-row>
                            <rkm-col cols="6">
                                <rkm-form-input-autocomplete
                                    :disabled="model.finalizada"
                                    :repository="repositories.fornecedor"
                                    :search-fields="autocompleteFields.fornecedor"
                                    :validations="validations.fornecedor"
                                    label="Fornecedor"
                                    uppercase
                                    text-field="nomeFantasia"
                                    v-model="model.documento.fornecedor"
                                >
                                    <rkm-input-group-append slot="append">
                                        <rkm-button
                                            :disabled="model.finalizada"
                                            @click="$refs.ModalFornecedor.open()"
                                            color="primary"
                                            title="Cadastrar fornecedor"
                                            v-rkm-tooltip.hover
                                        >
                                            <rkm-icon icon="plus" />
                                        </rkm-button>
                                    </rkm-input-group-append>
                                </rkm-form-input-autocomplete>
                            </rkm-col>

                            <rkm-col cols="3">
                                <rkm-form-select
                                    :disabled="model.finalizada || model.itens.length > 0"
                                    :options="form.tiposDocumento"
                                    :validations="validations.documentoTipo"
                                    label="Tipo"
                                    show-default-option
                                    text-field="nome"
                                    v-model="model.documento.tipoId"
                                    value-field="id"
                                />
                            </rkm-col>

                            <rkm-col cols="3">
                                <rkm-form-input
                                    :disabled="model.finalizada"
                                    :validations="validations.numeroPedido"
                                    label="Número do pedido"
                                    number
                                    v-model="model.documento.numeroPedido"
                                />
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col cols="3">
                                <rkm-form-input
                                    :disabled="model.finalizada"
                                    :validations="validations.numeroDocumento"
                                    label="Número"
                                    number
                                    v-model="model.documento.numero"
                                />
                            </rkm-col>

                            <rkm-col cols="3">
                                <rkm-form-date-picker
                                    :disabled="model.finalizada || model.itens.length > 0"
                                    :max-date="additionals.dataAtual"
                                    :validations="validations.dataDocumento"
                                    label="Data"
                                    v-model="model.documento.data"
                                />
                            </rkm-col>
                        </rkm-row>
                    </rkm-form>
                </rkm-area>

                <Material
                    :data="{ additionals, form, model }"
                    :disabled="!model.documento.data || !model.documento.tipoId"
                    ref="Material"
                />
            </rkm-form>

            <ModalFornecedor
                @save="model.documento.fornecedor = $event"
                ref="ModalFornecedor"
            />
        </template>
    </div>
</template>

<script>
// Components.
import Material from './components/material/Index'
import ModalFornecedor from '@/components/cadastro/fornecedor/Modal'

// Vuex.
import { mapActions } from 'vuex'

// Mixins.
import { repository, error, stringCase, object, data, formats, unmask } from '@/mixins'

// Moment.
import moment from 'moment'

export default {
    components: { Material, ModalFornecedor },
    computed: {
        autocompleteFields() {

            return {
                fornecedor: [
                    { key: 'nome_fantasia', searchable: true },
                ],
            }
        },
        validations() {

            return {
                fornecedor: {
                    required: true,
                },
                documentoTipo: {
                    required: true,
                },
                numeroPedido: {
                    required: true,
                    max: 20,
                },
                numeroDocumento: {
                    required: true,
                    max: 10,
                },
                dataDocumento: {
                    required: true,
                },
            }
        },
    },
    created() {

        this.repositories = {
            fornecedor: this.getConectaSusRepository('cadastro.fornecedor'),
            entrada: this.getConectaSusRepository('almoxarifado.entrada'),
            balancete: this.getConectaSusRepository('almoxarifado.balancete'),
        }

        this.load()
    },
    data: () => ({
        additionals: null,
        form: null,
        loaded: false,
        model: null,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            const request = this.id
                ? this.repositories.entrada.edit(this.id)
                : this.repositories.entrada.create()

            request
                .then(({ data }) => {

                    if (data.model.documento.data) {

                        data.model.documento.data = moment(data.model.documento.data, 'DD/MM/YYYY').toDate()
                    }

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.addWatchData()
                    this.$emit('model-load', this.model)

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchData() {

            this.$watch('model.documento.data', function (data) {

                if (data) {

                    this.verificarBalancete()
                }
                else {

                    this.$nextTick().then(() => this.$refs.Material.$refs.Form.resetForm())
                }
            })
        },
        finalizar() {

            this.showConfirmation(
                'Após finalizar não será mais possível alterar os dados do documento. Deseja continuar?',
                async () => this.save(true)
            )
        },
        save(finalizar) {

            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    if (this.model.itens.length === 0) {

                        this.showValidationNotification('Obrigatório incluir pelo menos um material.')

                        return;
                    }

                    this.setLoading()

                    const request = this.id
                        ? this.repositories.entrada.update(this.id, this.prepareData(finalizar))
                        : this.repositories.entrada.store(this.prepareData(finalizar))

                    request
                        .then(({ data }) => {

                            this.showSaveNotification()
                            this.$emit('save', data)
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        prepareData(finalizar) {

            const data = this.clone(this.model)

            data.finalizada = finalizar

            this.createIdKeysToObject(data.documento, [
                'fornecedor',
            ])

            this.formateDates(data.documento, [
                'data',
            ], 'DD/MM/YYYY')

            data.itens.map(item => {

                delete item.estornoSalvo

                item.quantidade = parseInt(item.quantidade)
                item.valorTotal = this.formatSumMoney(item.valorTotal)
                item.valorUnitario = this.formatSumMoney(item.valorUnitario)
                item.despesas = this.formatSumMoney(item.despesas)

                if (item.numeroAutorizacaoFornecimento) {

                    item.numeroAutorizacaoFornecimento = this.unMaskNumeroAutorizacaoFornecimento(
                        item.numeroAutorizacaoFornecimento
                    )
                }

                this.createIdKeysToObject(item, [
                    'material',
                    'fabricante',
                ])

                this.formateDates(item, [
                    'validadeLote'
                ], 'DD/MM/YYYY')
            })

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        verificarBalancete() {

            const data = moment(this.model.documento.data).format('YYYY-MM-DD')

            this.setLoading()

            this.repositories.balancete.checkDate(data)
                .then(({ data }) => {

                    if (!data.mesFechado) return

                    this.showValidationNotification('A data inserida corresponde a um mês já finalizado.')

                    this.model.documento.data = null
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        }
    },
    mixins: [ repository, error, stringCase, object, data, formats, unmask ],
    props: {
        id: { type: Number }
    },
}
</script>